import { useEffect, useState } from 'react';

import type { TelegramWebApp } from '../types/telegram-webapp';

/** Get a handle on the Telegram WebApp client SDK, sync the document
 *  ``data-theme`` attribute to its colorScheme, and propagate updates
 *  when the user toggles their system theme.
 *
 *  When the page is not opened from inside Telegram (local browser dev),
 *  ``tg`` is ``null`` and theme falls back to the browser's
 *  ``prefers-color-scheme`` media query — that way the dev experience
 *  isn't permanently stuck in light mode.
 */
export function useTelegramWebApp(): {
  tg: TelegramWebApp | null;
  theme: 'light' | 'dark';
  setTheme: (next: 'light' | 'dark') => void;
} {
  const [tg] = useState<TelegramWebApp | null>(() => window.Telegram?.WebApp ?? null);

  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (tg) return tg.colorScheme;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Bootstrap the WebApp + subscribe to theme changes coming from
  // Telegram (user toggles system theme while the Mini App is open).
  useEffect(() => {
    if (!tg) return;
    tg.ready();
    tg.expand();
    const onThemeChange = () => setTheme(tg.colorScheme);
    tg.onEvent('themeChanged', onThemeChange);
    return () => tg.offEvent('themeChanged', onThemeChange);
  }, [tg]);

  // Mirror the resolved theme onto <html data-theme> so the CSS token
  // blocks in tokens.css activate.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return { tg, theme, setTheme };
}
